/**
 * Logger wrapper. Routes to Sentry in stg/prod and to the console in dev.
 * Existing ESLint rule forbids raw `console.log`; this is the sanctioned path.
 */
import * as Sentry from '@sentry/react';

type Level = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

function emit(level: Level, message: string, context?: LogContext): void {
  if (level === 'error') {
    Sentry.captureMessage(message, { level: 'error', extra: context });
    console.error(message, context);
    return;
  }
  if (level === 'warn') {
    Sentry.captureMessage(message, { level: 'warning', extra: context });
    console.warn(message, context);
    return;
  }
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console[level === 'debug' ? 'log' : 'info'](message, context);
  }
}

export const logger = {
  debug: (msg: string, ctx?: LogContext) => emit('debug', msg, ctx),
  info: (msg: string, ctx?: LogContext) => emit('info', msg, ctx),
  warn: (msg: string, ctx?: LogContext) => emit('warn', msg, ctx),
  error: (err: unknown, ctx?: LogContext) => {
    if (err instanceof Error) {
      Sentry.captureException(err, { extra: ctx });
      console.error(err.message, err, ctx);
    } else {
      emit('error', String(err), ctx);
    }
  },
};
